
// ABOUTME: Component displaying available subscription plans with pricing and features
// ABOUTME: Handles plan selection and checkout initiation

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Check, Star } from 'lucide-react';
import { useSubscriptions } from '@/hooks/useSubscriptions';

const SubscriptionPlans = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const { plans, plansLoading, createCheckout, createCheckoutLoading, subscriptionStatus } = useSubscriptions();

  const handleSelectPlan = (planId: string) => {
    createCheckout({ planId, billingCycle });
  };

  const getCurrentPlan = () => {
    if (!subscriptionStatus?.hasSubscription) return null;
    // Logic to determine current plan would go here
    return null;
  };

  const currentPlan = getCurrentPlan();

  if (plansLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="h-32 bg-gray-200 rounded-t-lg" />
            <CardContent className="h-64 bg-gray-100" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center space-x-4">
        <span className={billingCycle === 'monthly' ? 'font-semibold' : 'text-gray-500'}>
          Monthly
        </span>
        <Switch
          checked={billingCycle === 'yearly'}
          onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
        />
        <span className={billingCycle === 'yearly' ? 'font-semibold' : 'text-gray-500'}>
          Yearly
        </span>
        {billingCycle === 'yearly' && (
          <Badge variant="secondary" className="ml-2">Save 17%</Badge>
        )}
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan, index) => {
          const isPopular = index === 1; // Middle plan is popular
          const isCurrentPlan = currentPlan?.id === plan.id;
          const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
          const yearlyPrice = plan.price_yearly;
          const monthlyEquivalent = yearlyPrice ? yearlyPrice / 12 : 0;

          return (
            <Card key={plan.id} className={`relative ${isPopular ? 'border-blue-500 shadow-lg scale-105' : ''}`}>
              {isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-500 text-white px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              {isCurrentPlan && (
                <div className="absolute -top-4 right-4">
                  <Badge variant="default">Current Plan</Badge>
                </div>
              )}

              <CardHeader className={`text-center ${isPopular ? 'bg-gradient-to-r from-blue-50 to-indigo-50' : ''} rounded-t-lg`}>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <p className="text-gray-600">{plan.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold">${price}</span>
                  <span className="text-gray-500">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  {billingCycle === 'yearly' && (
                    <div className="text-sm text-gray-500 mt-1">
                      ${monthlyEquivalent.toFixed(0)}/month billed annually
                    </div>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Check className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  className="w-full" 
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={createCheckoutLoading || isCurrentPlan}
                  variant={isPopular ? "default" : "outline"}
                >
                  {isCurrentPlan ? 'Current Plan' : `Choose ${plan.name}`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Enterprise Contact */}
      <div className="text-center mt-8 p-6 bg-gray-50 rounded-lg">
        <h3 className="text-xl font-semibold mb-2">Need a custom solution?</h3>
        <p className="text-gray-600 mb-4">
          Contact our sales team for enterprise pricing and custom features.
        </p>
        <Button variant="outline">Contact Sales</Button>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
